import React, { Fragment, ReactNode, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import Button from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'warning' | 'danger' | 'success';
  closeOnClickOutside?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  variant = 'default',
  closeOnClickOutside = true
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (closeOnClickOutside && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, closeOnClickOutside]);
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Early return if modal is not open
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  const variantClasses = {
    default: 'border-card-border',
    warning: 'border-amber-700/30',
    danger: 'border-red-700/30',
    success: 'border-emerald-700/30'
  };

  const headerGradients = {
    default: 'from-gray-800 to-gray-900',
    warning: 'from-amber-700 to-amber-800',
    danger: 'from-red-700 to-red-800',
    success: 'from-emerald-700 to-emerald-800'
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className={`
                w-full ${sizeClasses[size]} transform overflow-hidden rounded-xl 
                bg-background-card
                text-left align-middle shadow-2xl transition-all
                border ${variantClasses[variant]} relative
              `}>
                {/* Subtle background pattern overlay */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(75,85,99,0.05)_1px,transparent_1px)] bg-[length:20px_20px] opacity-20 pointer-events-none"></div>
                
                {/* Top border accent */}
                <div className={`h-1 w-full bg-gradient-to-r ${headerGradients[variant]}`}></div>
                
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-card-border sticky top-0 z-10 bg-background-card">
                  <Dialog.Title as="h3" className="text-lg font-medium font-display text-gray-200">
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-full p-1.5 text-gray-400 hover:text-gray-200 bg-background-darker hover:bg-gray-700/50 transition-colors"
                    onClick={onClose}
                  >
                    <X size={18} />
                  </button>
                </div>
                
                {/* Modal body */}
                <div className="p-4 overflow-y-auto max-h-[calc(100vh-8rem)]">
                  {children}
                </div>
                
                {/* Footer */}
                {footer && (
                  <div className="px-6 py-4 border-t border-card-border bg-background-darker flex justify-end space-x-3 sticky bottom-0 z-10">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const ModalFooter: React.FC<{ 
  onCancel: () => void;
  onConfirm: () => void;
  cancelText?: string;
  confirmText?: string;
  isLoading?: boolean;
  confirmVariant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'success';
}> = ({ 
  onCancel, 
  onConfirm, 
  cancelText = 'Cancel', 
  confirmText = 'Confirm',
  isLoading = false,
  confirmVariant = 'primary'
}) => {
  return (
    <>
      <Button variant="glass" onClick={onCancel} disabled={isLoading} size="sm">
        {cancelText}
      </Button>
      <Button variant={confirmVariant} onClick={onConfirm} isLoading={isLoading} size="sm">
        {confirmText}
      </Button>
    </>
  );
};

export default Modal; 